import { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { AdminAccessCard } from "./AdminAccessCard";
import { ActivityFeed, CityBreakdown, ContactMessages, MarketingAssets, MilestonesCard, OverviewStats } from "./OverviewCards";
import { EmailSettingsCard, WaitlistTable } from "./WaitlistTable";

const REFRESH_MS = 20000;

export const OverviewTab = ({ onExpire, onError }) => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [waitlist, contact, milestones, activity, settings] = await Promise.all([
        adminApi.get("/waitlist"),
        adminApi.get("/contact"),
        adminApi.get("/admin/milestones"),
        adminApi.get("/admin/activity"),
        adminApi.get("/admin/settings"),
      ]);
      setData({
        entries: waitlist.data,
        messages: contact.data,
        milestones: milestones.data,
        activity: activity.data,
        nudgeHours: settings.data.referral_nudge_hours,
      });
      onError("");
    } catch (e) {
      if (e?.response?.status === 401) onExpire();
      else onError("Could not load the waitlist. Please try again.");
    }
  }, [onExpire, onError]);

  useEffect(() => {
    load();
    const refresh = () => {
      if (!document.hidden) load();
    };
    const id = setInterval(refresh, REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  const entries = data?.entries;

  const cityStats = useMemo(() => {
    if (!entries) return [];
    const counts = {};
    entries.forEach((e) => {
      const c = e.city || "Not specified";
      counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [entries]);

  const filtered = useMemo(() => {
    if (!entries) return [];
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (cityFilter !== "all" && (e.city || "Not specified") !== cityFilter) return false;
      if (!q) return true;
      return e.email.toLowerCase().includes(q) || (e.city || "").toLowerCase().includes(q);
    });
  }, [entries, search, cityFilter]);

  const deleteEntry = async (id) => {
    if (!window.confirm("Remove this person from the waitlist?")) return;
    setDeletingId(id);
    try {
      await adminApi.delete(`/waitlist/${id}`);
      setData((prev) => ({ ...prev, entries: prev.entries.filter((e) => e.id !== id) }));
    } catch (e) {
      if (e?.response?.status === 401) onExpire();
      else onError("Could not delete that entry. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!data) {
    return <p className="py-20 text-center text-cream/50" data-testid="admin-loading">Loading the waitlist…</p>;
  }

  return (
    <>
      <OverviewStats total={entries.length} cityStats={cityStats} />
      <AdminAccessCard />
      <CityBreakdown cityStats={cityStats} />
      <MilestonesCard milestones={data.milestones} />
      <WaitlistTable
        filtered={filtered}
        cityStats={cityStats}
        search={search}
        setSearch={setSearch}
        cityFilter={cityFilter}
        setCityFilter={setCityFilter}
        onDelete={deleteEntry}
        deletingId={deletingId}
      />
      <MarketingAssets />
      <EmailSettingsCard key={data.nudgeHours} initialHours={data.nudgeHours} onSaved={load} />
      <ActivityFeed activity={data.activity} />
      <ContactMessages messages={data.messages} />
    </>
  );
};
